import { useEffect, useState } from 'react';
import Select from 'react-select';
import translations from '../../../../../../translations';
import type { PageCondition } from '../../../../../../types/page';
import { isNullOrEmpty } from '../../../../../../utils/guard';
import { getSharedStyles } from '../../../../../../utils/select';
import ButtonDefault from '../../../../../buttons/ButtonDefault';
import ButtonPrimary from '../../../../../buttons/ButtonPrimary';
import FormGroup from '../../../../../generic/FormGroup';
import { useNotificationDispatch } from '../../../../../notifications/NotificationProvider';
import { PAGE_CONDITION_ACTION_OPTIONS, SUMMARY_CONTEXT } from '../../../../constants';
import { usePageEditor } from '../../../PageEditorProvider';
import ConfigSection from '../../composer/config-panel/ConfigSection';
import PageConditionItemList from '../common/PageConditionItemList';
import Portal from '../common/Portal';
import RadioToggle from '../common/RadioToggle';
import { usePageCondition } from '../PageConditionsProvider';
import PageConditionOutcomeConfig from './PageConditionOutcomeConfig';
import PageConditionOperationConfig from './pageConditionOperationConfig/PageConditionOperationConfig';
import PageConditionRuleConfig from './pageConditionRuleConfig/PageConditionRuleConfig';

const ruleComparisonOptions = [
    { label: 'AND', value: 'AND' },
    { label: 'OR', value: 'OR' },
];

const getInitialComparison = (condition: PageCondition | null) => {
    const comparison = condition?.comparisonType;
    return comparison
        ? ruleComparisonOptions.find((option) => option.value === comparison)
        : ruleComparisonOptions[0];
};

const PageConditionConfig = () => {
    const { state: pageBuilderState } = usePageEditor();
    const { tab } = pageBuilderState;
    const {
        state: pageConditionState,
        updateConditionIndex,
        updateRuleIndex,
        updateOperationIndex,
        updateComparison,
        updateProgressOutcomeComponent,
        applyCondition,
    } = usePageCondition();
    const { condition, conditionIndex, ruleIndex, operationIndex } = pageConditionState;
    const footerPortalId = `sidebar-right-footer-portal-${tab?.key}`;

    const dispatchNotification = useNotificationDispatch();

    const [ruleComparison, setRuleComparison] = useState<
        { label: string; value: string } | undefined | null
    >(getInitialComparison(condition));

    // A condition that has an Outcomes component against it moves the flow on rather than changing the page, so that is
    // the action we show it as having
    const [conditionAction, setConditionAction] = useState(
        isNullOrEmpty(condition?.progressOutcomePageComponentId)
            ? PAGE_CONDITION_ACTION_OPTIONS['OPERATIONS']
            : PAGE_CONDITION_ACTION_OPTIONS['OUTCOME'],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        if (ruleComparison) {
            updateComparison(ruleComparison.value);
        }
    }, [ruleComparison]);

    const onCancel = () => {
        updateConditionIndex(null);
        updateRuleIndex(null);
        updateOperationIndex(null);
    };

    const onApply = () => {
        // Check that we got at least one rule.
        const hasRules = condition?.pageRules?.length;

        if (hasRules) {
            applyCondition();
            onCancel();
        } else {
            dispatchNotification({
                type: 'notifyError',
                payload: { error: translations.PAGE_BUILDER_page_condition_not_configured_message },
            });
        }
    };

    // A condition either progresses the flow on an outcome or executes operations, so switching to operations
    // clears the Outcomes component the condition was going to progress on.
    const onUpdateConditionAction = (selectedAction: string) => {
        if (
            selectedAction === PAGE_CONDITION_ACTION_OPTIONS['OPERATIONS'] &&
            !isNullOrEmpty(condition?.progressOutcomePageComponentId)
        ) {
            updateProgressOutcomeComponent(null);
        }

        setConditionAction(selectedAction);
    };

    const onAddNewRule = () => updateRuleIndex(-1);
    const onAddNewOperation = () => updateOperationIndex(-1);

    const renderConditionAction = () => {
        if (conditionAction === PAGE_CONDITION_ACTION_OPTIONS['OUTCOME']) {
            return <PageConditionOutcomeConfig />;
        }

        if (operationIndex !== null) {
            return <PageConditionOperationConfig />;
        }

        return (
            <>
                <PageConditionItemList listContext={SUMMARY_CONTEXT['OPERATIONS']} />
                <ButtonDefault
                    className="align-left"
                    onClick={onAddNewOperation}
                    title="Add new page condition operation"
                >
                    Add operation
                </ButtonDefault>
            </>
        );
    };

    return (
        <ConfigSection dataTestId="condition-editor">
            <h4 className="sidebar-section-heading">
                {conditionIndex !== null && conditionIndex >= 0
                    ? 'Edit condition'
                    : 'New condition'}
            </h4>
            {/* RULES */}
            {ruleIndex === null ? (
                <>
                    <PageConditionItemList listContext={SUMMARY_CONTEXT['RULES']} />
                    <ButtonDefault
                        className="align-left"
                        onClick={onAddNewRule}
                        title="Add new page condition operation"
                    >
                        Add rule
                    </ButtonDefault>
                </>
            ) : (
                <PageConditionRuleConfig />
            )}
            {/* RULE COMPARISON */}
            <FormGroup
                label="Combine page rules using"
                htmlFor="rule-comparison-type"
                isValid={!isNullOrEmpty(ruleComparison)}
                validationMessage={translations.PAGE_BUILDER_field_is_required_validation_message}
                showValidation={true}
                isRequired
            >
                <Select
                    inputId="rule-comparison-type"
                    className="select-field"
                    styles={getSharedStyles<
                        (typeof ruleComparisonOptions)[number] | null | undefined
                    >()}
                    options={ruleComparisonOptions}
                    onChange={(selectedComparison) => setRuleComparison(selectedComparison)}
                    placeholder="Available options"
                    value={ruleComparison}
                    noOptionsMessage={() => 'No results found'}
                />
            </FormGroup>
            <br />

            {/* CONDITION ACTION */}
            <RadioToggle
                options={PAGE_CONDITION_ACTION_OPTIONS}
                selectedOption={conditionAction}
                updateSelectedOption={onUpdateConditionAction}
                testId="page-condition-action-toggle"
            />
            <br />

            {/* OPERATIONS OR OUTCOME */}
            {renderConditionAction()}
            <br />

            <Portal wrapperId={footerPortalId}>
                <footer className="sidebar-section-footer">
                    <ButtonDefault onClick={onCancel} title="Cancel condition">
                        Cancel
                    </ButtonDefault>
                    <ButtonPrimary onClick={onApply} title="Apply condition">
                        Apply
                    </ButtonPrimary>
                </footer>
            </Portal>
        </ConfigSection>
    );
};

export default PageConditionConfig;
